import { z } from 'zod'
import { LatLngSchema } from '../locations/latLng'
import { idSchemaV1, idSchemaV2, isId, TeamIdSchema, UserIdSchema } from '../ids'
import { AuditPropsSchema } from '../common'
import { ArchivableSchema } from '../archive'
import { MeetingScheduleRuleSchema } from './schedule'
import { MeetingOccurrenceIdSchema, MeetingOccurrenceStatusSchema } from './occurrenceId'

export const AttendIdSchema = idSchemaV1('Attend')
export type AttendId = z.infer<typeof AttendIdSchema>

export const MeetingIdSchema = idSchemaV2('Meeting')
export type MeetingId = z.infer<typeof MeetingIdSchema>
export const isMeetingId = isId(MeetingIdSchema)

export const WithMeetingIdSchema = z.object({
  meetingId: MeetingIdSchema,
})
export type WithMeetingId = z.infer<typeof WithMeetingIdSchema>

export const MeetingHistoryItemSchema = z.object({
  day: z.number(),
  hasCheckin: z.boolean(),
  /**
   * for user: 0 = no checkin, 1 = has checkin for 1 team, 2 = has checkin for 2 teams, etc.
   * for team: 0 -> 1 : 0 -> 100% (percentage of team members with checkin)
   */
  value: z.number(),
  timestamp: z.number(),
})
export type MeetingHistoryItem = z.infer<typeof MeetingHistoryItemSchema>

export const MeetingAttendanceStatusSchema = z.enum(['pending', 'checked_in', 'missed', 'not_attending'])
export type MeetingAttendanceStatus = z.infer<typeof MeetingAttendanceStatusSchema>

export const MeetingNudgeSchema = z.object({
  managerId: UserIdSchema,
  nudgedAt: z.number(),
})
export type MeetingNudge = z.infer<typeof MeetingNudgeSchema>

export const TeamMeetingHistoryItemSchema = z.object({
  meetingId: MeetingIdSchema,
  teamId: TeamIdSchema,
  occurrenceId: MeetingOccurrenceIdSchema.optional(),
  originalScheduledStart: z.number().optional(),
  scheduledStart: z.number(),
  status: MeetingOccurrenceStatusSchema.optional().meta({
    description: 'cancelled occurrences have no eligible members and never count as missed',
  }),
  attendanceCount: z.number(),
  eligibleCount: z.number(),
  attendanceRate: z.number(),
})
export type TeamMeetingHistoryItem = z.infer<typeof TeamMeetingHistoryItemSchema>

export const MeetingAttendanceItemSchema = z.object({
  meetingId: MeetingIdSchema,
  teamId: TeamIdSchema,
  userId: UserIdSchema,
  occurrenceId: MeetingOccurrenceIdSchema.optional(),
  originalScheduledStart: z.number().meta({
    description:
      'Original scheduled start in epoch milliseconds, used to identify the occurrence before any reschedule.',
  }),
  scheduledStart: z.number(),
  status: MeetingAttendanceStatusSchema,
  checkinDate: z.number().optional(),
  nudges: MeetingNudgeSchema.array().optional(),
  nextNudgeAt: z.number().optional().meta({
    description:
      'Absolute nudge cooldown deadline in epoch milliseconds. Absent when never nudged; may be in the past.',
  }),
})
export type MeetingAttendanceItem = z.infer<typeof MeetingAttendanceItemSchema>

export const MeetingLocationSchema = z.object({
  location: LatLngSchema.optional().meta({
    description: 'Check-in location. If omitted, the team office location is used when maxDistance is set.',
  }),
  maxDistance: z.number().positive().optional().meta({
    description: 'Maximum check-in distance in meters. If omitted, check-in is allowed from anywhere.',
  }),
})
export type MeetingLocation = z.infer<typeof MeetingLocationSchema>

export const MeetingCheckinOptionsSchema = z.object({
  minutesBefore: z.number().int().nonnegative().optional(),
  minutesAfter: z.number().int().nonnegative().optional(),
})
export type MeetingCheckinOptions = z.infer<typeof MeetingCheckinOptionsSchema>

export const MeetingReminderSchema = z.object({
  offsetMinutes: z.number().int().meta({
    description: 'Minutes relative to scheduled start. Negative values fire before the start.',
    example: -10,
  }),
})
export type MeetingReminder = z.infer<typeof MeetingReminderSchema>

export const UnsavedMeetingSchema = z.object({
  ...ArchivableSchema.shape,
  teamId: TeamIdSchema,
  name: z.string().min(1).max(100),
  downline: z.boolean().optional().meta({
    description: 'When true, the meeting applies to this team and its downline teams.',
  }),
  location: MeetingLocationSchema.optional(),
  schedule: MeetingScheduleRuleSchema.array().min(1),
  checkinOptions: MeetingCheckinOptionsSchema.optional(),
  meetingUrl: z.url().optional().meta({
    description: 'Optional meeting link opened at check-in (for example a video call URL).',
  }),
  reminders: MeetingReminderSchema.array(),
})
export type UnsavedMeeting = z.infer<typeof UnsavedMeetingSchema>

export const MeetingSchema = z.object({
  ...AuditPropsSchema.shape,
  ...WithMeetingIdSchema.shape,
  ...UnsavedMeetingSchema.omit({ reminders: true }).shape,
  reminders: MeetingReminderSchema.array(),
})
export type Meeting = z.infer<typeof MeetingSchema>
